import { useEffect, useState } from 'react';
import Custombutton from '../../../../../components/custombutton/custombutton';
import { commaNumber, getErrorMessage } from '../../../../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import CustomModal from '../../../../../components/CustomModal';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { mutateConfig } from '../../../../../api/queryBase';
import { toast } from 'react-toastify';
import { Beneficiary } from '../../interfaces/Beneficiary';
import { Wallet } from '../../../../../interfaces/Wallet';
import { PaymentRequest } from '../../interfaces/PaymentRequest';
import { useUserProfile } from '../../../../../customhooks/useUserProfile';
import { AxiosError } from 'axios';
import Button from '../../../../../components/button/Button';

interface ConfirmationProps {
    currencyFrom: string;
    currencyTo: string;
    amountToSend: number;
    amountToReceive: number;
    updateStep: (activeStep: number) => void;
    conversionType: number;
    requestObject: PaymentRequest;
    rate: number;
    recipientChoosen: Beneficiary & {
        bankName?: string;
        accountNumber?: string;
    };
    sourceWallet?: Wallet;
}

const Confirmation = ({
    currencyFrom,
    currencyTo,
    amountToSend,
    amountToReceive,
    updateStep,
    conversionType,
    requestObject,
    rate,
    recipientChoosen,
    sourceWallet
}: ConfirmationProps) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const { userDetails } = useUserProfile();

    const isAdmin =
        userDetails?.role?.toLowerCase() === 'admin' ||
        userDetails?.role?.toLowerCase() === 'owner';

    const [error, setError] = useState<string | any>(null);
    const [rateChange, setRateChange] = useState(false);
    const [isMakingPayment, setIsMakingPayment] = useState(false);
    const [expectedNewRate, setexpectedNewRate] = useState(null);
    const [transactionReference, setTransactionReference] = useState('');
    const mutation = mutateConfig({});

    const { mutate: approveTransaction, isLoading: isApproving } = mutation({
        url: `/transactions/approve/${transactionReference}`,
        cacheKeys: 'approvals'
    });

    const { mutate: initiateTransaction, isLoading: isInitiating } = mutation({
        url: '/transactions/initiate'
    });

    const handleApprove = () => {
        approveTransaction(
            { expectedRate: rate },
            {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    setIsMakingPayment(false);
                    if (error) {
                        toast.error(
                            getErrorMessage(error) || 'An error occurred.'
                        );
                        return;
                    }
                    navigate('/payments/transactions', {
                        state: {
                            newTransactionAlert: true
                        }
                    });

                    toast.info('Transaction successful.');
                }
            }
        );
    };

    const onHandleSubmit = () => {
        setIsMakingPayment(true);

        initiateTransaction(
            {
                destinationCurrencyCode: currencyTo,
                expectedDestinationAmount: Number(amountToReceive),
                expectedRate: rate,
                paymentPurposeId: requestObject.purposeId || '',
                paymentReference: requestObject.reference ?? undefined,
                notifyParties: requestObject.notifyParties || [],
                sourceAmount: Number(amountToSend),
                sourceCurrencyCode: currencyFrom,
                transactionDestination: recipientChoosen?.id,
                transactionSource: sourceWallet?.id,
                transactionDocumentsUrl: [
                    requestObject.transactionDocumentsUrl
                ],
                transactionFee: 0,
                transactionType:
                    conversionType === 2
                        ? 1
                        : currencyFrom === currencyTo
                        ? 2
                        : 3
            },
            {
                onSettled: (response, error) => {
                    if (error) {
                        toast.error(
                            getErrorMessage(error) || 'An error occurred.'
                        );
                        setIsMakingPayment(false);
                        return;
                    }
                    const {
                        payload: { reference }
                    } = response as unknown as {
                        payload: { reference: string };
                    };

                    if (isAdmin && reference) {
                        setTransactionReference(reference);
                    } else {
                        navigate('/payments/approvals', {
                            state: {
                                newTransactionAlert: true
                            }
                        });

                        setIsMakingPayment(false);

                        toast.info('Transaction awaiting approval.');
                    }
                }
            }
        );
    };

    useEffect(() => {
        if (transactionReference && isAdmin) {
            handleApprove();
        }
    }, [transactionReference]);

    return (
        <div className='w-[500px]'>
            <div className=' px-[16px] pt-[40px] lg:pt-0 rounded-[3px]'>
                {error !== null && (
                    <Alert
                        variant='outlined'
                        severity='error'
                        sx={{ marginBottom: '16px' }}
                    >
                        {error}
                    </Alert>
                )}

                <div className=' border-b border-appcolorgrey pb-[0.8rem]'>
                    <div className='flex justify-between mb-4'>
                        <p className='font-medium text-base text-ebony'>
                            Transfer details
                        </p>
                        <button
                            className='cursor-pointer text-appcolorprimary text-sm  font-semibold  w-fit'
                            onClick={() => {
                                updateStep(0);
                            }}
                        >
                            Edit
                        </button>
                    </div>

                    <div className='flex justify-between  mb-4'>
                        <p className='text-sm leading-6'>You send</p>
                        <p className='text-ebony text-sm leading-6 font-bold'>
                            {commaNumber(amountToSend)}
                            {' ' + currencyFrom}
                        </p>
                    </div>

                    <div className='flex justify-between  mb-4'>
                        <p className='text-sm leading-6'>
                            Total Fees (included)
                        </p>
                        <p className='text-ebony text-sm leading-6'>
                            0.00 {currencyFrom}
                        </p>
                    </div>

                    <div className='flex justify-between  mb-4'>
                        <p className='text-sm leading-6'>SWIFT Charge</p>
                        <p className=' text-ebony text-sm leading-6'>
                            25.00 USD
                        </p>
                    </div>
                    <div className='flex justify-between  mb-4'>
                        <p className='text-sm leading-6'>
                            Guaranteed rate (30secs)
                        </p>
                        <p className=' text-ebony text-sm leading-6'>
                            1 &nbsp;
                            {currencyFrom}
                            &nbsp; = {Number(rate).toFixed(7)}
                            &nbsp;
                            {currencyTo}
                        </p>
                    </div>
                    <div className='flex justify-between '>
                        <p className='text-sm leading-6'>
                            {conversionType === 1
                                ? recipientChoosen?.beneficiaryCompanyName
                                : 'You'}
                            &nbsp;
                            {conversionType === 1 ? `gets` : 'get'}
                            &nbsp; exactly
                        </p>
                        <p className='text-ebony text-sm leading-6 font-bold'>
                            {commaNumber(amountToReceive)} {currencyTo}
                        </p>
                    </div>
                </div>

                <div className=' flex justify-center my-[16px] h-[24px] items-center'>
                    <p className='w-[56px] border border-0 border-b border-silver'></p>
                </div>

                {conversionType === 1 && (
                    <>
                        <div className=' pb-[0.8rem]'>
                            <div className='flex justify-between  mb-4'>
                                <p className='font-medium text-base text-ebony'>
                                    Recipients details
                                </p>
                                <button
                                    className='cursor-pointer text-appcolorprimary text-sm  font-semibold  w-fit'
                                    onClick={() => {
                                        updateStep(1);
                                    }}
                                >
                                    Change
                                </button>
                            </div>

                            <div className='flex justify-between  mb-4'>
                                <p className='text-sm leading-6'>Name</p>
                                <p className='text-ebony text-sm leading-6'>
                                    {recipientChoosen?.beneficiaryCompanyName}
                                </p>
                            </div>

                            <div className='flex justify-between  mb-4'>
                                <p className='text-sm leading-6'>Bank Name</p>
                                <p className='text-ebony text-sm leading-6'>
                                    {recipientChoosen?.bankName}
                                </p>
                            </div>

                            <div className='flex justify-between  '>
                                <p className='text-sm leading-6'>Acc Number</p>
                                <p className='text-ebony text-sm leading-6'>
                                    {recipientChoosen?.accountNumber}
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className='flex justify-center my-[16px] h-[24px] items-center'>
                <p className='w-[56px] border border-0 border-b border-silver'></p>
            </div>

            {conversionType === 1 && (
                <>
                    <div className='px-[16px]  rounded-[3px]'>
                        <div className='flex justify-between mb-2'>
                            <p className='text-sm leading-6'>Reference</p>
                            <button
                                className='cursor-pointer text-appcolorprimary text-sm  font-semibold  w-fit'
                                onClick={() => {
                                    updateStep(1);
                                }}
                            >
                                Edit
                            </button>
                        </div>

                        <div className='flex justify-between  mb-2'>
                            <p className='text-sm leading-6'>
                                {!requestObject?.reference
                                    ? 'No Reference Provided'
                                    : requestObject?.reference}
                            </p>
                        </div>
                    </div>
                </>
            )}

            <div className='mt-8 px-[16px] lg:px-0 mb-20 lg:mb-0'>
                <Button
                    text='Confirm and send'
                    onClick={onHandleSubmit}
                    isLoading={isMakingPayment}
                    type='submit'
                    className='px-[0.92rem] w-full'
                />
            </div>

            {rateChange && expectedNewRate !== null && (
                <CustomModal
                    maxWidth='xs'
                    open={rateChange}
                    onClose={() => setRateChange(false)}
                    title='Rate Change'
                    subTitle={
                        <span>
                            Rate changed from &nbsp;
                            <span className='text-appcolorprimary'>
                                1 &nbsp;
                                {currencyFrom}
                                &nbsp; = {Number(rate).toFixed(7)}
                                &nbsp;
                                {currencyTo}
                            </span>{' '}
                            &nbsp; to &nbsp;
                            <span className='text-appcolorprimary'>
                                1 &nbsp;
                                {currencyFrom}
                                &nbsp; = {Number(expectedNewRate).toFixed(7)}
                                &nbsp;
                                {currencyTo}
                            </span>
                            , would you like to continue with the new rate?
                        </span>
                    }
                    styles={{
                        overflowX: 'hidden'
                    }}
                >
                    <div className=''>
                        <Grid
                            container
                            spacing={4}
                            sx={{
                                marginTop: '0px'
                            }}
                        >
                            <Grid item xs={6} md={6} lg={6}>
                                <Custombutton
                                    type='button'
                                    fullWidth
                                    variant='contained'
                                    buttonText='Cancel'
                                    styles={{
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        padding:
                                            '0.65rem 0.92rem 0.6rem 0.93rem',
                                        backgroundColor: 'transparent',
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                    onClickAction={() => {
                                        setRateChange(false);
                                        setexpectedNewRate(null);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6} md={6} lg={6}>
                                <Custombutton
                                    isLoadingButton
                                    type='button'
                                    fullWidth
                                    variant='contained'
                                    buttonText='Proceed'
                                    styles={{
                                        padding:
                                            '0.65rem 0.92rem 0.6rem 0.93rem'
                                    }}
                                    onClickAction={onHandleSubmit}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </CustomModal>
            )}
        </div>
    );
};

export default Confirmation;
